import React, { useState, useEffect } from 'react';
import { Button } from "./ui/Button";
import { Spinner } from "./ui/Spinner";
import { X, ExternalLink } from 'lucide-react';

interface LogViewerModalProps {
  logUrl: string;
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  type?: 'ai' | 'execution';
}

export const LogViewerModal: React.FC<LogViewerModalProps> = ({
  logUrl,
  isOpen,
  onClose,
  title = "Log Viewer",
  type = 'ai'
}) => {
  const [logContent, setLogContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure the DOM is updated before adding the visible class
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 10);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const fetchLogContent = async () => {
      if (!logUrl) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const response = await fetch(logUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch log content: ${response.statusText}`);
        }
        
        const text = await response.text();
        setLogContent(text);
      } catch (err) {
        console.error('Error fetching log content:', err);
        setError(err instanceof Error ? err.message : 'Failed to load log content');
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchLogContent();
    }
  }, [logUrl, isOpen]);

  const formatCSVContent = (content: string) => {
    const lines = content.split('\n').filter(line => line.trim());
    if (lines.length === 0) return null;

    const headers = lines[0].split('*').map(header => header.trim());
    const rows = lines.slice(1).map(line => {
      const values = line.split('*').map(value => value.trim());
      return values;
    });

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {headers.map((header, index) => (
                <th
                  key={index}
                  className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                {row.map((cell, cellIndex) => (
                  <td
                    key={cellIndex}
                    className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap"
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Overlay */}
      <div 
        className={`fixed inset-0 bg-black/30 backdrop-blur-sm transition-opacity duration-300 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />
      
      {/* Side panel */}
      <div 
        className={`fixed right-0 top-0 h-full w-1/2 bg-white shadow-lg transform transition-transform duration-300 ease-in-out flex flex-col ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="text-xl font-semibold">{title}</h2>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(logUrl, '_blank')}
              className="flex items-center gap-1"
            >
              <ExternalLink className="h-4 w-4" />
              Open in New Tab
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="flex justify-center items-center h-full">
              <Spinner className="w-8 h-8" />
            </div>
          ) : error ? (
            <div className="p-4 bg-red-50 text-red-700 rounded-md">
              <p>{error}</p>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 overflow-auto max-h-full">
              {type === 'execution' && logContent ? (
                formatCSVContent(logContent) || 'No content available'
              ) : (
                <pre className="font-mono text-sm whitespace-pre-wrap">
                  {logContent || 'No content available'}
                </pre>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 